import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { getSocket } from "../utils/socket";
import PageMeta from "../components/PageMeta";
import ModernMapTiles from "../components/ModernMapTiles";

interface FriendLoc {
  id: string;
  name: string;
  avatar_url?: string;
  live_latitude: number;
  live_longitude: number;
  live_location_updated_at: string;
}

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const friendIcon = (avatarUrl: string | undefined, name: string) =>
  L.divIcon({
    html: `<div style="width:44px;height:44px;border-radius:50%;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3);background:${avatarUrl ? `url(${avatarUrl}) center/cover` : "#0a0a0a"};display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:14px;">${avatarUrl ? "" : initials(name)}</div>`,
    className: "",
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });

function Recenter({ center }: { center: [number, number] | null }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 13, { duration: 0.6 }); }, [center, map]);
  return null;
}

export default function FriendsMap() {
  const navigate = useNavigate();
  const { user, token } = useApp();
  const [friends, setFriends] = useState<FriendLoc[]>([]);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef<L.Map | null>(null);

  const myCenter = useMemo<[number, number] | null>(
    () => (user?.latitude && user?.longitude ? [user.latitude, user.longitude] : null),
    [user?.latitude, user?.longitude]
  );

  const load = () => {
    api.get("/location/friends").then(data => setFriends(data?.friends || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!token) return;
    const socket = getSocket(token);
    const onUpdate = (payload: any) => {
      setFriends(prev => {
        const exists = prev.some(f => f.id === payload.user_id);
        const updated: FriendLoc = { id: payload.user_id, name: payload.name, avatar_url: payload.avatar_url, live_latitude: payload.latitude, live_longitude: payload.longitude, live_location_updated_at: payload.updated_at };
        return exists ? prev.map(f => (f.id === payload.user_id ? updated : f)) : [...prev, updated];
      });
    };
    socket.on("friend_location_update", onUpdate);
    return () => { socket.off("friend_location_update", onUpdate); };
  }, [token]);

  const center: [number, number] = myCenter || (friends[0] ? [friends[0].live_latitude, friends[0].live_longitude] : [48.8566, 2.3522]);

  return (
    <>
      <PageMeta title="Carte des amis" />
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
          <span style={{ fontSize: 20, fontWeight: 800 }}>Carte des amis</span>
        </div>

        {!loading && friends.length === 0 && (
          <div style={{ padding: "16px 20px 0", fontSize: 13, color: "#999" }}>
            Aucun ami ne partage sa position avec toi pour le moment. Va dans Réglages → Partage de position pour activer le tien.
          </div>
        )}

        <div style={{ flex: 1, position: "relative" }}>
          <MapContainer center={center} zoom={12} style={{ width: "100%", height: "100%" }} ref={mapRef as any}>
            <ModernMapTiles />
            <Recenter center={myCenter} />
            {myCenter && (
              <Marker position={myCenter} icon={friendIcon(user?.avatar_url, "Moi")} />
            )}
            {friends.map(f => (
              <Marker key={f.id} position={[f.live_latitude, f.live_longitude]} icon={friendIcon(f.avatar_url, f.name)}>
                <Popup>
                  <div style={{ textAlign: "center", minWidth: 140 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: "#999", marginBottom: 8 }}>
                      Vu {Math.max(0, Math.round((Date.now() - new Date(f.live_location_updated_at).getTime()) / 60000))} min
                    </div>
                    <button onClick={() => navigate(`/profile/${f.id}`)} style={{ border: "1px solid #E0E0E0", borderRadius: 16, padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "white", cursor: "pointer" }}>
                      Voir le profil
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </>
  );
}
